import { DiscoverResourceTableView } from 'features/discover-resources';
import { DiscoverResourceByOrganizationTableView } from 'features/discover-resources-by-organization';
import { JobSeekerResourcesTableView, ResourcesViewMode } from 'features/job-seeker-resources/types';
import { ResourceDetails, GetResourceDetailsTypes } from 'features/resource-details';
import { ResourceShareForm, ResourceShareFormData } from 'features/resource-share';
import { GetResourcesTable, ResourceFormData, ResourceTableView, GetResourceFormDataTypes } from 'features/resources/types';
import { JobSeekerResourcesRequestData, JobSeekerResourcesFiltersData, SetResourceLike, ResourceCommentForm, SetResourceEventRsvpFormData, ResourceEventRsvpForm } from 'features/job-seeker-resources/types';
import { GetResourceNetworksModerationTable, ResourceNetworksModerationTableResponse } from 'features/resource-networks-moderation';
import { PostNetworkApprovalForm, GetPostNetworksModerationTabsFilters } from 'features/post-networks-moderation/types';
import { TableDataResponse } from 'features/types';
import { client, download, withDataRequest } from 'utils/client';
import { Tab } from './types';

const resourcesAPI = {
  getFormData(resourceId: string | undefined, resourcesViewMode: ResourcesViewMode, resourceFormDataType: GetResourceFormDataTypes) {
    let endpoint = '';
    switch (resourceFormDataType) {
      case GetResourceFormDataTypes.Organozation:
        endpoint = `/resources/form-data/${resourcesViewMode}`;
        if (resourceId) endpoint = `/resources/${resourceId}/form-data/${resourcesViewMode}`;
        return client.get<ResourceFormData>(endpoint);
      case GetResourceFormDataTypes.JobSeeker:
        endpoint = `/resources/form-data-for-job-seeker/${resourcesViewMode}`;
        if (resourceId) endpoint = `/resources/${resourceId}/form-data-for-job-seeker/${resourcesViewMode}`;
        return client.get<ResourceFormData>(endpoint);
      default:
        return Promise.reject(new Error(`Unknown getResourceFormDataType: ${resourceFormDataType}`));
    }
  },
  addJob(data: FormData, resourcesViewMode: ResourcesViewMode, resourceFormDataType: GetResourceFormDataTypes) {
    switch (resourceFormDataType) {
      case GetResourceFormDataTypes.Organozation:
        return client.post<void>(`/resources/add/${resourcesViewMode}`, withDataRequest(data));
      case GetResourceFormDataTypes.JobSeeker:
        return client.post<void>(`/resources/add-for-job-seeker/${resourcesViewMode}`, withDataRequest(data));
      default:
        return Promise.reject(new Error(`Unknown getResourceFormDataType: ${resourceFormDataType}`));
    }
  },
  editJob(data: FormData, resourceId: string, resourcesViewMode: ResourcesViewMode, resourceFormDataType: GetResourceFormDataTypes) {
    switch (resourceFormDataType) {
      case GetResourceFormDataTypes.Organozation:
        return client.put<void>(`/resources/${resourceId}/edit/${resourcesViewMode}`, withDataRequest(data));
      case GetResourceFormDataTypes.JobSeeker:
        return client.put<void>(`/resources/${resourceId}/edit-for-job-seeker/${resourcesViewMode}`, withDataRequest(data));
      default:
        return Promise.reject(new Error(`Unknown getResourceFormDataType: ${resourceFormDataType}`));
    }
  },
  getTable(params: GetResourcesTable) {
    return client.post<TableDataResponse<ResourceTableView>>('/resources/get-table', withDataRequest(params));
  },
  getJobSeekerResourcesTabsData(params: JobSeekerResourcesRequestData) {
    return client.post<Tab[]>('/resources/get-job-seeker-resources-tabs', withDataRequest(params));
  },
  getJobSeekerResourcesFiltersData(resourcesViewMode: ResourcesViewMode) {
    return client.get<JobSeekerResourcesFiltersData>(`/resources/get-job-seeker-resources-filters-data/${resourcesViewMode}`);
  },
  archiveJobSeekerResource(resourceId: string) {
    return client.post<void>(`/resources/${resourceId}/archive-job-seeker-resource`);
  },
  setJobSeekerResourceLike(resourceId: string) {
    return client.post<SetResourceLike>(`/resources/${resourceId}/mark-as-likes-job-seeker-resource`);
  },
  setJobSeekerResourceRsvp(resourceId: string, params: ResourceEventRsvpForm) {
    return client.post<SetResourceEventRsvpFormData>(`/resources/${resourceId}/set-rsvp-job-seeker-resource`, withDataRequest(params));
  },
  getDetails(resourceId: string, getResourceDetailsType: GetResourceDetailsTypes) {
    switch (getResourceDetailsType) {
      case GetResourceDetailsTypes.Discover:
        return client.get<ResourceDetails>(`/resources/${resourceId}/discover-details`, undefined, false);
      case GetResourceDetailsTypes.Community:
        return client.get<ResourceDetails>(`/resources/${resourceId}/details-for-community`);
      case GetResourceDetailsTypes.JobSeeker:
        return client.get<ResourceDetails>(`/resources/${resourceId}/details-for-job-seeker`);
      default:
        return client.get<ResourceDetails>(`/resources/${resourceId}/details`);
    }
  },
  getModerationDetails(resourceId: string) {
    return client.get<ResourceDetails>(`/resources/${resourceId}/details-for-moderation`);
  },
  addEditComment(data: ResourceCommentForm) {
    return client.post<ResourceDetails>('/resources/add-edit-comment', withDataRequest(data));
  },
  deleteComment(id: string, commentId: string) {
    return client.delete<void>(`/resources/${id}/${commentId}/delete-comment`);
  },
  downloadFile(resourceId: string, discover: boolean) {
    if (discover) return download(`/resources/${resourceId}/discover-download-file`, undefined, false);
    else return download(`/resources/${resourceId}/download-file`);
  },
  getJobSeekerTable(params: GetResourcesTable) {
    return client.post<TableDataResponse<JobSeekerResourcesTableView>>('/resources/get-job-seeker-table', withDataRequest(params));
  },
  getDiscoverTable(params: GetResourcesTable) {
    return client.post<TableDataResponse<DiscoverResourceTableView>>('/resources/get-discover-table', withDataRequest(params), false);
  },
  getDiscoverByOrganizationTable(params: GetResourcesTable) {
    return client.post<TableDataResponse<DiscoverResourceByOrganizationTableView>>('/resources/get-discover-by-organization-table', withDataRequest(params));
  },
  getShareFormData(resourceId: string) {
    return client.get<ResourceShareFormData>(`/resources/${resourceId}/share-form-data`);
  },
  share(params: ResourceShareForm, resourceId: string) {
    return client.put<void>(`/resources/${resourceId}/share`, withDataRequest(params));
  },
  unshare(resourceId: string) {
    return client.put<void>(`/resources/${resourceId}/unshare`);
  },
  getResourceNetworksModerationTabs(params: GetPostNetworksModerationTabsFilters) {
    return client.post<Tab[]>('/resources/get-resource-networks-moderation-tabs', withDataRequest(params));
  },
  getResourceNetworksModerationTable(params: GetResourceNetworksModerationTable) {
    return client.post<ResourceNetworksModerationTableResponse>('/resources/get-resource-networks-moderation-table', withDataRequest(params))
  },
  approveResource(resourceId: string, data: PostNetworkApprovalForm) {
    return client.put<void>(`/resources/${resourceId}/resource-networks-moderation-approve`, withDataRequest(data));
  },
  getResourceNetworksModerationCount() {
    return client.get<number>(`/resources/resource-networks-moderation-count`);
  },
};

export default resourcesAPI;
